import React from 'react';
import { Camera, Star, FileText, Baby, Heart, Check } from 'lucide-react';

const ContentFilter = ({ 
  contentFilter, 
  onContentFilterChange,
  entries,
  milestones,
  babyBook 
}) => {
  const toggleFilter = (key) => {
    onContentFilterChange({
      ...contentFilter,
      [key]: !contentFilter[key]
    });
  };

  // Calculate counts for each content type
  const counts = {
    photos: entries?.reduce((sum, e) => sum + (e.photos?.length || 0), 0) || 0,
    journalEntries: entries?.filter(e => e.text && !e.milestone).length || 0,
    milestones: milestones?.length || 0,
    preBirth: [
      babyBook?.howTheyMet,
      babyBook?.howFoundOut,
      babyBook?.nameStory,
      babyBook?.pregnancyNotes,
      babyBook?.cravings,
      babyBook?.babyShower
    ].filter(Boolean).length,
    birthDetails: babyBook?.birthDate ? 1 : 0
  };

  const filterOptions = [
    {
      key: 'photos',
      label: 'Photos',
      description: 'Include all photos from entries',
      icon: Camera,
      count: counts.photos,
      countLabel: 'photos'
    },
    {
      key: 'journalEntries',
      label: 'Journal Entries',
      description: 'Written memories and notes',
      icon: FileText,
      count: counts.journalEntries,
      countLabel: 'entries'
    },
    {
      key: 'milestones',
      label: 'Milestones',
      description: 'First smile, first steps, etc.',
      icon: Star,
      count: counts.milestones,
      countLabel: 'milestones'
    },
    {
      key: 'preBirth',
      label: 'Pre-Birth Story',
      description: 'How you met, pregnancy journey, name story',
      icon: Heart,
      count: counts.preBirth,
      countLabel: 'sections'
    },
    {
      key: 'birthDetails',
      label: 'Birth Details',
      description: 'Date, time, weight, hospital',
      icon: Baby,
      count: counts.birthDetails,
      countLabel: counts.birthDetails ? 'included' : 'not set'
    }
  ];

  const selectedCount = Object.values(contentFilter).filter(Boolean).length;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-ocean-900">
          What to Include
        </h3>
        <span className="text-sm text-gray-500">
          {selectedCount} of {filterOptions.length} selected
        </span>
      </div>

      <div className="space-y-2">
        {filterOptions.map((option) => {
          const Icon = option.icon;
          const isEnabled = contentFilter[option.key];
          const hasContent = option.count > 0;
          
          return (
            <button
              key={option.key}
              onClick={() => toggleFilter(option.key)}
              disabled={!hasContent}
              className={`w-full flex items-center gap-3 p-3 rounded-lg border-2 transition-all text-left ${
                !hasContent
                  ? 'border-gray-100 bg-gray-50 opacity-50 cursor-not-allowed'
                  : isEnabled
                    ? 'border-ocean-500 bg-ocean-50'
                    : 'border-gray-200 bg-white hover:border-ocean-300'
              }`}
            >
              {/* Checkbox */}
              <div className={`w-6 h-6 rounded-md flex items-center justify-center flex-shrink-0 ${
                isEnabled && hasContent
                  ? 'bg-ocean-500'
                  : 'bg-gray-200'
              }`}>
                {isEnabled && hasContent && (
                  <Check className="w-4 h-4 text-white" />
                )}
              </div>

              {/* Icon */}
              <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                isEnabled && hasContent
                  ? 'bg-ocean-100'
                  : 'bg-gray-100'
              }`}>
                <Icon className={`w-5 h-5 ${
                  isEnabled && hasContent ? 'text-ocean-600' : 'text-gray-400'
                }`} />
              </div>

              {/* Text */}
              <div className="flex-1 min-w-0">
                <p className={`font-medium ${
                  isEnabled && hasContent ? 'text-ocean-900' : 'text-gray-700'
                }`}>
                  {option.label}
                </p>
                <p className="text-xs text-gray-500 truncate">
                  {option.description}
                </p>
              </div>

              {/* Count */}
              <div className={`text-right flex-shrink-0 ${
                isEnabled && hasContent ? 'text-ocean-600' : 'text-gray-400'
              }`}>
                <p className="font-semibold">{option.count}</p>
                <p className="text-xs">{option.countLabel}</p>
              </div>
            </button>
          );
        })}
      </div>

      {/* Quick actions */}
      <div className="flex gap-2 pt-2">
        <button
          onClick={() => onContentFilterChange({
            photos: true,
            journalEntries: true,
            milestones: true,
            preBirth: true,
            birthDetails: true
          })}
          className="flex-1 py-2 px-3 text-sm font-medium text-ocean-700 bg-ocean-100 rounded-lg hover:bg-ocean-200 transition-colors"
        >
          Select All
        </button>
        <button
          onClick={() => onContentFilterChange({
            photos: false,
            journalEntries: false,
            milestones: false,
            preBirth: false,
            birthDetails: false
          })}
          className="flex-1 py-2 px-3 text-sm font-medium text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
        >
          Clear All
        </button>
      </div>

      {/* Estimated page count */}
      <div className="bg-gray-50 rounded-lg p-3 text-center">
        <p className="text-sm text-gray-600">
          Estimated book size: <span className="font-semibold text-ocean-900">
            {Math.max(10, Math.ceil(
              (contentFilter.photos ? counts.photos / 4 : 0) +
              (contentFilter.journalEntries ? counts.journalEntries / 3 : 0) +
              (contentFilter.milestones ? 2 : 0) +
              (contentFilter.preBirth ? 2 : 0) +
              (contentFilter.birthDetails ? 1 : 0) +
              4 // cover, title, back cover, buffer
            ))}
          </span> pages
        </p>
      </div>
    </div>
  );
};

export default ContentFilter;